import { t } from './translations';
import { elevenLabsTTS } from './premiumTTS';
export { elevenLabsSTT } from './elevenLabsSTT';

/**
 * Simplified Text-to-Speech Service
 * Uses ElevenLabs with automatic language detection (eleven_multilingual_v2)
 * Falls back to browser TTS if ElevenLabs is not available
 */
class TextToSpeechService {
  private synth: SpeechSynthesis;
  private isSupported: boolean;
  private voices: SpeechSynthesisVoice[] = [];
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  constructor() {
    this.synth = window.speechSynthesis;
    this.isSupported = 'speechSynthesis' in window;
    
    if (this.isSupported) {
      this.loadVoices();
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  private loadVoices(): void {
    this.voices = this.synth.getVoices();
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private detectLanguage(text: string): string {
    const portuguese = /[àáâãçéêíóôõú]/i;
    const spanish = /[ñáéíóúü]/i;
    
    if (portuguese.test(text)) {
      return 'pt-BR';
    } else if (spanish.test(text)) {
      return 'es-ES';
    } else {
      return 'en-US';
    }
  }

  private getVoiceByLanguage(lang: string): SpeechSynthesisVoice | null {
    let voice = this.voices.find(v => v.lang === lang);
    
    if (!voice) {
      const langCode = lang.split('-')[0];
      voice = this.voices.find(v => v.lang.startsWith(langCode));
    }
    
    return voice || null;
  }

  /**
   * Speak text using ElevenLabs (auto language detection) or browser TTS fallback
   */
  async speak(text: string): Promise<void> {
    // Try ElevenLabs first (with automatic language detection)
    if (elevenLabsTTS.isAvailable()) {
      try {
        await elevenLabsTTS.speak(text);
        return;
      } catch (error) {
        console.warn('Premium TTS failed, falling back to browser TTS:', error);
      }
    }

    // Fallback to browser TTS
    return this.speakWithBrowserTTS(text);
  }

  private speakWithBrowserTTS(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSupported) {
        reject(new Error(t('tts.notSupported')));
        return;
      }

      this.stop();

      const cleanText = this.stripHtml(text);
      
      if (!cleanText.trim()) {
        reject(new Error(t('tts.emptyText')));
        return;
      }

      const utterance = new SpeechSynthesisUtterance(cleanText);
      this.currentUtterance = utterance;

      const lang = this.detectLanguage(cleanText);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      const voice = this.getVoiceByLanguage(lang);
      if (voice) {
        utterance.voice = voice;
      }
      utterance.lang = lang;

      utterance.onend = () => {
        this.currentUtterance = null;
        resolve();
      };

      utterance.onerror = (event) => {
        this.currentUtterance = null;
        reject(new Error(`TTS Error: ${event.error}`));
      };

      try {
        this.synth.speak(utterance);
      } catch (error) {
        this.currentUtterance = null;
        reject(error);
      }
    });
  }

  stop(): void {
    elevenLabsTTS.stop();
    
    if (this.isSupported && this.synth.speaking) {
      this.synth.cancel();
      this.currentUtterance = null;
    }
  }

  isSpeaking(): boolean {
    return this.isSupported && this.synth.speaking;
  }

  getIsSupported(): boolean {
    return this.isSupported || elevenLabsTTS.isAvailable();
  }
}

// Create and export singleton instance
export const ttsService = new TextToSpeechService();
export default ttsService;